import { Component, Inject, OnDestroy } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import {
	fetchCpfDependentStart,
	fetchEmailDependentStart,
	createDependentClear,
	createDependentStart,
	restoreFormReset,
} from '../../../../sanus/organizations/state/organizations.actions';
import {
	selectCpfDependent,
	selectDepModalResult,
	selectEmailDependent,
	selectRestoreForm,
} from '../../../../sanus/organizations/state/organizations.selector';
import { Gender } from '../../../../shared/enums/gender.enum';
import { TypeKinship } from '../../../../shared/enums/type-kinship.enum';
import { DataTypeUtils } from '../../../../utils/data-type.util';
import { PT_BR_DATE_FORMATS } from '../../../../utils/date-formats.utils';
import { normalizeEmptyToNull } from '../../../../utils/fields.utils';
import { PhoneUtils } from '../../../../utils/phone-utils';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-add-dependent-modal',
	styleUrls: ['./add-dependent-modal.component.scss'],
	templateUrl: './add-dependent-modal.component.html',
})
export class AddDependentModalComponent implements OnDestroy {
	cpfPattern = '[0-9]{11}';

	dependentTypes = [
		{ label: 'Cônjuge', value: TypeKinship.CONJUGE },
		{ label: 'Filho', value: TypeKinship.FILHO },
		{ label: 'Enteado', value: TypeKinship.ENTEADO },
		{ label: 'Pai / Mãe', value: TypeKinship.PAI_MAE },
		{ label: 'Neto', value: TypeKinship.NETO },
		{ label: 'Irmão', value: TypeKinship.IRMAO },
		{ label: 'Avô / Avó', value: TypeKinship.AVO },
		{ label: 'Tio / Tia', value: TypeKinship.TIO },
		{ label: 'Sobrinho', value: TypeKinship.SOBRINHO },
		{ label: 'Bisneto', value: TypeKinship.BISNETO },
		{ label: 'Sogro', value: TypeKinship.SOGRO },
		{ label: 'Genro / Nora', value: TypeKinship.GENRO_NORA },
		{ label: 'Padrasto / Madrasta', value: TypeKinship.PADRASTO_MADRASTA },
		{ label: 'Outros', value: TypeKinship.OUTROS },
	];
	form: UntypedFormGroup;

	genders: Gender[] = Object.values(Gender);

	inputId: string = String(Math.random());

	isLoading = false;

	phoneMask = (value: string): string => {
		this.form.get('phone')?.updateValueAndValidity();

		return value ? PhoneUtils.phoneMask(value) : '';
	};

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		public dialogRef: MatDialogRef<AddDependentModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: string,
	) {
		this.form = this.fb.group({
			birthday: [null, [Validators.required]],
			cpf: [null, [Validators.required, Validators.pattern(this.cpfPattern)]],
			cpf_titular: [
				null,
				[Validators.required, Validators.pattern(this.cpfPattern)],
			],
			email: [null, [Validators.email]],
			gender: [null, []],
			inclusion_day: [null, []],
			marriageDate: [null],
			mothers_name: [null, []],
			name: [null, [Validators.required]],
			phone: [null, [Validators.pattern(DataTypeUtils.phone.pattern)]],
			type: [null, [Validators.required]],
		});

		this.form.get('marriageDate')?.disable();
		if (this.form.get('type')?.value === TypeKinship.CONJUGE) {
			this.form.get('marriageDate')?.enable();
		}
	}

	checkDependentType(): void {
		const marriageDateControl = this.form.get('marriageDate');

		if (this.form.get('type')?.value != TypeKinship.CONJUGE) {
			marriageDateControl?.disable();
			marriageDateControl?.setValue(null);
		} else {
			marriageDateControl?.enable();
		}
	}

	closeModal() {
		this.dialogRef.close(false);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(restoreFormReset());
	}

	onContinue(): void {
		if (this.form.valid) {
			const request = this.form.value;
			request.email = normalizeEmptyToNull(request.email);
			request.phone = normalizeEmptyToNull(request.phone);
			request.birthday = moment(request.birthday).format(
				'YYYY-MM-DD hh:mm:ssZZ',
			);

			if (request.inclusion_day && moment(request.inclusion_day).isValid()) {
				request.inclusion_day = moment(request.inclusion_day).format(
					'YYYY-MM-DD hh:mm:ssZZ',
				);
			} else {
				request.inclusion_day = null;
			}

			request.marriageDate = request.marriageDate
				? moment(request.marriageDate).format('YYYY-MM-DD hh:mm:ssZZ')
				: null;
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.isLoading = false;
					}
				});
			this.store.select(selectDepModalResult).subscribe((success) => {
				if (success) {
					this.store.dispatch(createDependentClear());
					this.dialogRef.close(true);
				}
			});
			this.isLoading = true;
			this.store.dispatch(
				createDependentStart({ cpf: '', orgId: this.data, request }),
			);
		}
	}

	onCpfDependentBlur(): void {
		if (this.form.get('cpf')!.valid) {
			this.store.dispatch(
				fetchCpfDependentStart({
					cpf: this.form.get('cpf')!.value,
					orgId: this.data,
				}),
			);
			this.store
				.select(selectCpfDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('cpf')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('cpf')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onEmailBlur(): void {
		const emailValue = this.form.get('email')!.value;

		if (emailValue && this.form.get('email')!.valid) {
			this.store.dispatch(
				fetchEmailDependentStart({
					email: emailValue,
					orgId: this.data,
				}),
			);
			this.store
				.select(selectEmailDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('email')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('email')!.setErrors({ minlength: true });
					}
				});
		}
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
